import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const empty = { author: "", quotation: "", exact_source: "", work: "", page: "", tradition: "", theme: "", verification_status: "Unverified", rights_notes: "" };
const statuses = ["Unverified", "Verified", "Approved for publication", "Do not use"];

export default function AdminQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/quotes").then(({ data }) => setQuotes(data.quotes));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!form.author || !form.quotation) { toast("Author and quotation are required."); return; }
    if (editing) { await api.patch(`/admin/quotes/${editing}`, form); toast("Quote updated."); }
    else { await api.post("/admin/quotes", form); toast("Quote added."); }
    setForm(empty); setEditing(null); load();
  };
  const edit = (q) => { setEditing(q.id); setForm({ ...empty, ...q }); window.scrollTo(0, 0); };
  const del = async (id) => { await api.delete(`/admin/quotes/${id}`); load(); };

  const inputCls = "w-full bg-transparent border border-ink/25 px-3 py-2 font-body text-ink outline-none";

  return (
    <div className="p-6 md:p-10 max-w-4xl">
      <p className="mono-label mb-3">The Quote Library</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Words for the Way</h1>
      <p className="font-body text-ink-secondary mt-4 leading-relaxed max-w-2xl">Only quotes marked <strong>Approved for publication</strong> appear publicly. Never fabricate or misattribute a quotation — always record the exact source.</p>

      <div className="border border-ink/15 p-6 mt-8 space-y-4 bg-paper-secondary">
        <h2 className="font-heading text-2xl text-ink">{editing ? "Edit quote" : "Add a quote"}</h2>
        <label className="block"><span className="mono-label block mb-1">Quotation</span><textarea rows={3} value={form.quotation} onChange={(e) => set("quotation", e.target.value)} className={inputCls} data-testid="quote-text" /></label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="mono-label block mb-1">Author / teacher</span><input value={form.author} onChange={(e) => set("author", e.target.value)} className={inputCls} data-testid="quote-author" /></label>
          <label className="block"><span className="mono-label block mb-1">Tradition</span><input value={form.tradition} onChange={(e) => set("tradition", e.target.value)} className={inputCls} data-testid="quote-tradition" /></label>
          <label className="block"><span className="mono-label block mb-1">Work / book</span><input value={form.work} onChange={(e) => set("work", e.target.value)} className={inputCls} /></label>
          <label className="block"><span className="mono-label block mb-1">Exact source</span><input value={form.exact_source} onChange={(e) => set("exact_source", e.target.value)} className={inputCls} /></label>
          <label className="block"><span className="mono-label block mb-1">Page</span><input value={form.page} onChange={(e) => set("page", e.target.value)} className={inputCls} /></label>
          <label className="block"><span className="mono-label block mb-1">Theme</span><input value={form.theme} onChange={(e) => set("theme", e.target.value)} className={inputCls} /></label>
        </div>
        <label className="block"><span className="mono-label block mb-1">Rights / copyright notes</span><input value={form.rights_notes} onChange={(e) => set("rights_notes", e.target.value)} className={inputCls} /></label>
        <label className="block"><span className="mono-label block mb-1">Verification status</span>
          <select value={form.verification_status} onChange={(e) => set("verification_status", e.target.value)} className={inputCls} data-testid="quote-status">
            {statuses.map((s) => <option key={s}>{s}</option>)}
          </select></label>
        <div className="flex gap-3">
          <button onClick={save} className="btn-solid" data-testid="quote-save">{editing ? "Update" : "Add quote"}</button>
          {editing && <button onClick={() => { setForm(empty); setEditing(null); }} className="btn-ink">Cancel</button>}
        </div>
      </div>

      <div className="mt-10 space-y-3">
        {quotes.map((q) => (
          <div key={q.id} className="border border-ink/15 p-5 bg-paper" data-testid={`quote-row-${q.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="font-heading italic text-xl text-ink">“{q.quotation}”</p>
                <div className="mono-label mt-2">{q.author} · {q.tradition} · {[q.work, q.page && `p.${q.page}`].filter(Boolean).join(" ")}</div>
              </div>
              <span className={`stamp ${q.verification_status === "Approved for publication" ? "" : "opacity-60"}`} style={q.verification_status !== "Approved for publication" ? { borderColor: "#8A857D", color: "#8A857D" } : {}}>{q.verification_status}</span>
            </div>
            <div className="flex gap-4 mt-3">
              <button onClick={() => edit(q)} className="mono-label underline underline-offset-4" data-testid={`quote-edit-${q.id}`}>Edit</button>
              <button onClick={() => del(q.id)} className="mono-label underline underline-offset-4 text-terracotta" data-testid={`quote-delete-${q.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
